import { Button, HStack, Stack, Text } from "@chakra-ui/react";
import { Card } from "@components/ui-kit";
import { Plural, Trans } from "@lingui/macro";
import type {
  ExpenseWithoutOtherStructuredModel,
  OurWorkStructuredModel,
} from "@src/components/modules/invoices/Create/models";
import { useInvoiceStore } from "@src/components/modules/invoices/Create/store";
import { currency } from "@src/utils/formatters";
import { sumBy } from "lodash";
import { observer } from "mobx-react-lite";
import type React from "react";
import type { FC } from "react";

interface SectionProps {
  title: string;
  hide: boolean;
  item: OurWorkStructuredModel | ExpenseWithoutOtherStructuredModel;
}

const Section: FC<React.PropsWithChildren<SectionProps>> = ({
  title,
  hide,
  item,
  children,
}) => {
  const store = useInvoiceStore();
  if (hide) return null;
  const selectedItemsInSectionAmountSum = sumBy(
    item.budgetItems
      .map((i) =>
        i.__typename === "ExpenseBudgetItem"
          ? store.form.expense_budget_splits.$.get(i.id)
          : store.form.our_work_budget_splits.$.get(i.id),
      )
      .map((i) => (i === undefined ? 0 : i.$.amount.value.denominatedValue)),
  );

  if (hide) return null;

  return (
    <Card.Section>
      <HStack align="baseline" justify="space-between">
        <HStack align="baseline" spacing="2">
          <Text
            color="grey.600"
            fontWeight="semibold"
            textTransform="uppercase"
          >
            {title}
          </Text>
          {item.selectedCount && (
            <Text as="span" color="grey.500" fontSize="xs">
              <Plural
                value={item.selectedCount}
                one="(# item selected -"
                other="(# items selected -"
              />
              {` ${currency.formatByWorkspace(selectedItemsInSectionAmountSum)})`}
            </Text>
          )}
        </HStack>
        <Button
          onClick={() =>
            item.toggleSelectAll(undefined, item.budget_category_id)
          }
          size="sm"
          variant="link"
        >
          {item.allSelected ? (
            <Trans>Deselect all</Trans>
          ) : (
            <Trans>Select all</Trans>
          )}
        </Button>
      </HStack>

      <Stack py="4" spacing="3">
        {children}
      </Stack>
    </Card.Section>
  );
};

export default observer(Section);
